import type { AuditLogEntry } from '@/userManagement/domain/entities/AuditLogEntry';
import type { User } from '@/userManagement/domain/entities/User';
import type { UsersTabSummary } from '@/userManagement/domain/entities/UsersTabSummary';

import { collectModifiedPermissionTargetUsernamesInBaseline, isTimestampInUsersSummaryBaseline } from './usersSummaryBaselineWindow';

export interface ComputeUsersTabSummaryOptions {
  /**
   * TROC-281 / TROC-237: org-wide deviation count from `GET /users?hasRoleDeviation=true` totalCount.
   * Must not be derived from the first-page user slice (that undercounts).
   */
  usersWithRoleDeviationCount: number;
}

/** Pure aggregation for Users tab dashboard cards from whatever user + audit rows were fetched (first page when using default PageSize). */
export function computeUsersTabSummary(
  users: User[],
  auditEntries: AuditLogEntry[],
  options: ComputeUsersTabSummaryOptions
): UsersTabSummary {
  const usersAdded = auditEntries.filter((e) => e.action === 'CreatedUser' && isTimestampInUsersSummaryBaseline(e.timestamp)).length;
  /** TODO: surface when BE adds a removal event type to audit (e.g. DeletedUser). */
  const usersRemoved = 0;
  const permissionsChanged = auditEntries.filter(
    (e) => e.action === 'ModifiedPermission' && isTimestampInUsersSummaryBaseline(e.timestamp)
  ).length;
  const suspendedOrLocked = users.filter((u) => u.isSuspended || u.isLocked).length;
  const changedUsernames = collectModifiedPermissionTargetUsernamesInBaseline(auditEntries);
  const usersWithRecentPermissionChangeCount = users.filter((u) => changedUsernames.has(u.username.trim().toLowerCase())).length;

  return {
    usersAdded,
    usersRemoved,
    suspendedOrLocked,
    permissionsChanged,
    usersWithRoleDeviationCount: options.usersWithRoleDeviationCount,
    usersWithRecentPermissionChangeCount
  };
}
